import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { FormikHelpers } from 'formik';
import { load } from 'recaptcha-v3';

import media from '../../../utils/mediaQueries';
import { LINKS_COLOR, NOTES_COLOR } from '../../../theme';
import { getFormTexts, FORM_TEXTS } from './formDictionary';
import stores from '../../../stores';
import { REVIEWERS_TITLES } from './reviewerTypes';
import { StudentReviewForm } from './StudentReviewForm';
import { FunderReviewForm } from './FunderReviewForm';
import { UserSchool } from './types/UserSchool';
import { StudentFormValues } from './types/StudentFormValues';
import { FunderFormValues } from './types/FunderFormValues';
import { valuesToPayload } from './mappers';
import { ReviewerType } from '../../../enums';

const Body = styled.div`
  padding: 24px 40px 32px;
  ${media.mobile`
    padding: 16px 24px 24px;
  `}
  
  a {
    color: ${LINKS_COLOR};
  }
`;

const FormTitle = styled.h4`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.44;
  letter-spacing: 0.2px;
  margin-bottom: 10px;
`;

const Note = styled.div`
  font-size: 14px; 
  line-height: 1.29;
  letter-spacing: -0.1px;
  color: ${NOTES_COLOR};
  margin-bottom: 24px;
  
  ${media.mobile`
    font-size: 12px;
  `}
`;

type ReviewFormProps = {
  schoolId: string;
  userSchools?: UserSchool[];
  minCommentLength?: number;
};

type ReviewFormSubmitType = (values: StudentFormValues | FunderFormValues, {
  setErrors, setStatus, setSubmitting,
}: FormikHelpers<StudentFormValues> | FormikHelpers<FunderFormValues>) => void;

const ReviewFormComponent = (props: ReviewFormProps) => {
  const reviewTextRef = useRef(null);
  const { schoolId, userSchools, minCommentLength } = props;
  const { meetingStore } = stores;

  useEffect(() => {
    if (!(meetingStore.meetingSubjects.length || meetingStore.isMeetingSubjectsLoading)) {
      meetingStore.getMeetingSubjects(schoolId);
    }
  }, []);

  const [reviewerType, setReviewerType] = useState<ReviewerType>(ReviewerType.Student);

  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const reviewersOptions = Object.entries(REVIEWERS_TITLES).map(([key, value]: [key: ReviewerType, value: string]) => ({ id: key, name: value }));

  const getRecaptchaToken = useCallback(async () => {
    const recaptcha = await load(process.env.GOOGLE_RECAPTCHA_SITE_KEY);
    const token = await recaptcha.execute('review_post');

    if (token) {
      setRecaptchaToken(token);
    }
  }, []);

  useEffect(() => {
    getRecaptchaToken();
  }, []);

  const onFormSubmit: ReviewFormSubmitType = (values, { setErrors, setStatus, setSubmitting }) => {
    const reviewData = valuesToPayload(schoolId, values);
    const { stars } = values;
    stores.schoolReviewStore.setRating(stars);

    stores.schoolReviewStore.create(reviewData, setErrors, setStatus, setSubmitting, recaptchaToken);
  };

  const { title, titleNote } = useMemo<typeof FORM_TEXTS>(() => getFormTexts(''), []);

  return (
    <Body>
      <FormTitle>{title}</FormTitle>
      <Note>{titleNote}</Note>

      {reviewerType === ReviewerType.Student
        ? (
          <StudentReviewForm
            onFormSubmit={onFormSubmit}
            userSchools={userSchools}
            reviewTextRef={reviewTextRef}
            setReviewerType={setReviewerType}
            reviewersOptions={reviewersOptions}
            minCommentLength={minCommentLength}
          />
        )
        : (
          <FunderReviewForm
            onFormSubmit={onFormSubmit}
            userSchools={userSchools}
            reviewerType={reviewerType}
            setReviewerType={setReviewerType}
            reviewersOptions={reviewersOptions}
            reviewTextRef={reviewTextRef}
            minCommentLength={minCommentLength}
          />
        )}
    </Body>
  );
};

ReviewFormComponent.defaultProps = {
  userSchools: [],
  minCommentLength: 200,
};

const ReviewFormContainer = observer(ReviewFormComponent);

export { ReviewFormContainer };
